import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import { PageSpinner, ServerError } from '@ohpen/enso-ui';
import type { IConfig } from '@ohpen/react-utils';

import i18nReady from './i18n';
import { App } from './App';

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);

export async function bootstrap() {
  const container = document.getElementById('root');

  if (!container) {
    throw new Error('Root element with id "root" not found in the document.');
  }

  const root = createRoot(container);

  root.render(
    <StrictMode>
      <PageSpinner />
    </StrictMode>,
  );

  try {
    // react-i18next runs with the default useSuspense: true, so nothing may mount before i18n is ready.
    const [response] = await Promise.all([fetch('/config/config.json'), i18nReady]);

    if (!response.ok) {
      throw new Error(`Failed to load config: HTTP ${response.status}`);
    }

    const config: IConfig = await response.json();

    root.render(
      <StrictMode>
        <Suspense fallback={<PageSpinner />}>
          <App config={config} />
        </Suspense>
      </StrictMode>,
    );
  } catch (error) {
    console.error('Failed to bootstrap application:', error);
    root.render(
      <StrictMode>
        <ServerError />
      </StrictMode>,
    );
  }
}
